// /admin/i18n API surface (motiflink §1.5) — the M2 languages + translation
// overrides model. The legacy translation_keys client lives in ./translations.ts
// and is retired at the M9 cutover.

import type { i18n as i18nSchemas } from '@fajrarabi/zod-schemas';
import type { z } from 'zod';

import { API_BASE, api } from './api';

// Row shapes as the API returns them (Drizzle select + JSON serialisation, so
// timestamps arrive as ISO strings).
export interface LanguageRow {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface TranslationRow {
  id: string;
  translationKey: string;
  languageCode: string;
  translationValue: string;
  category: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CreateLanguageInput = z.input<typeof i18nSchemas.createLanguageInput>;
export type UpdateLanguageInput = z.input<typeof i18nSchemas.updateLanguageInput>;
export type UpsertTranslationInput = z.input<typeof i18nSchemas.upsertTranslationInput>;
export type UpdateTranslationInput = z.input<typeof i18nSchemas.updateTranslationInput>;

export interface ListTranslationsParams {
  lang?: string;
  category?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface TranslationPage {
  rows: TranslationRow[];
  total: number;
  limit: number;
  offset: number;
}

// The API wraps every i18n response in `{ data }` (CLAUDE.md §5 envelope).
type Envelope<T> = { data: T };

// ─── languages ──────────────────────────────────────────────────────────────

export async function listLanguages(signal?: AbortSignal): Promise<LanguageRow[]> {
  const { data } = await api<Envelope<LanguageRow[]>>('/admin/i18n/languages', { signal });
  return data;
}

export async function createLanguage(input: CreateLanguageInput): Promise<LanguageRow> {
  const { data } = await api<Envelope<LanguageRow>>('/admin/i18n/languages', {
    method: 'POST',
    body: input,
  });
  return data;
}

export async function updateLanguage(id: string, input: UpdateLanguageInput): Promise<LanguageRow> {
  const { data } = await api<Envelope<LanguageRow>>(`/admin/i18n/languages/${id}`, {
    method: 'PATCH',
    body: input,
  });
  return data;
}

export async function setDefaultLanguage(id: string): Promise<void> {
  await api<Envelope<{ ok: true }>>(`/admin/i18n/languages/${id}/set-default`, {
    method: 'PATCH',
  });
}

/** Full recomputed order — the API applies it as one atomic db.batch(). */
export async function reorderLanguages(
  order: Array<{ id: string; sortOrder: number }>,
): Promise<void> {
  await api<Envelope<{ ok: true }>>('/admin/i18n/languages/reorder', {
    method: 'PATCH',
    body: { order },
  });
}

export async function deleteLanguage(id: string): Promise<void> {
  await api<Envelope<{ ok: true }>>(`/admin/i18n/languages/${id}`, { method: 'DELETE' });
}

// ─── translations ───────────────────────────────────────────────────────────

export async function listTranslations(
  params: ListTranslationsParams = {},
  signal?: AbortSignal,
): Promise<TranslationPage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.size > 0 ? `?${qs.toString()}` : '';
  const { data } = await api<Envelope<TranslationPage>>(`/admin/i18n/translations${suffix}`, {
    signal,
  });
  return data;
}

/** Upsert on (key, language) — re-adding an existing pair edits it. */
export async function upsertTranslation(input: UpsertTranslationInput): Promise<TranslationRow> {
  const { data } = await api<Envelope<TranslationRow>>('/admin/i18n/translations', {
    method: 'POST',
    body: input,
  });
  return data;
}

export async function updateTranslation(
  id: string,
  input: UpdateTranslationInput,
): Promise<TranslationRow> {
  const { data } = await api<Envelope<TranslationRow>>(`/admin/i18n/translations/${id}`, {
    method: 'PATCH',
    body: input,
  });
  return data;
}

export async function deleteTranslation(id: string): Promise<void> {
  await api<Envelope<{ ok: true }>>(`/admin/i18n/translations/${id}`, { method: 'DELETE' });
}

export async function importTranslations(
  rows: UpsertTranslationInput[],
): Promise<{ imported: number }> {
  // The endpoint caps a request at 5000 rows; 500 keeps each POST comfortably
  // inside Neon's HTTP statement budget too (the route chunks at 200 server-side).
  const BATCH = 500;
  let imported = 0;
  for (let i = 0; i < rows.length; i += BATCH) {
    const { data } = await api<Envelope<{ imported: number }>>('/admin/i18n/translations/import', {
      method: 'POST',
      body: { rows: rows.slice(i, i + BATCH) },
    });
    imported += data.imported;
  }
  return { imported };
}

/**
 * Export URL for a plain anchor. Deliberately not a fetch+blob: the endpoint is
 * cookie-authed and sets Content-Disposition, so letting the browser navigate
 * gets the filename and the UTF-8 BOM (Excel-safe Arabic) for free.
 */
export function translationsExportUrl(lang?: string): string {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return `${API_BASE}/admin/i18n/translations/export${qs}`;
}
